import { Plus, Trash2 } from 'lucide-react'
import { newDisk } from '@/stores/infrastructureWizard'
import type { DataDisk } from '@/types/infrastructure'
import { diskProblems } from '@/utils/infraValidation'
import page from '@/pages/NewDeploymentPage.module.css'
import styles from './Infrastructure.module.css'
import { Button } from '@/components/common/Button'

const SKUS: { value: DataDisk['sku']; label: string }[] = [
  { value: 'Premium_LRS', label: 'Premium SSD (LRS)' },
  { value: 'Premium_ZRS', label: 'Premium SSD (ZRS)' },
  { value: 'StandardSSD_LRS', label: 'Standard SSD (LRS)' },
  { value: 'Standard_LRS', label: 'Standard HDD (LRS)' },
]
const CACHING: DataDisk['caching'][] = ['None', 'ReadOnly', 'ReadWrite']
const DRIVE_LETTERS = 'EFGHIJKLMNOPQRSTUVWXYZ'.split('')

interface DataDiskEditorProps {
  disks: DataDisk[]
  onChange: (disks: DataDisk[]) => void
  /** Names the group in labels and messages, for example "SQL server". */
  label: string
  idPrefix: string
}

/** Data disks, none or more: size, SKU, caching, drive letter and LUN. Optional: rows can be added and removed, and none is fine. */
export function DataDiskEditor({ disks, onChange, label, idPrefix }: DataDiskEditorProps) {
  const problems = diskProblems(disks)
  const patch = (key: string, change: Partial<DataDisk>) => onChange(disks.map((disk) => (disk.key === key ? { ...disk, ...change } : disk)))

  const add = () => {
    const usedLuns = new Set(disks.map((disk) => disk.lun))
    const usedLetters = new Set(disks.map((disk) => disk.driveLetter.toUpperCase()))
    let lun = 0
    while (usedLuns.has(lun)) lun += 1
    onChange([...disks, newDisk({ lun, driveLetter: DRIVE_LETTERS.find((letter) => !usedLetters.has(letter)) ?? 'E' })])
  }

  return (
    <fieldset className={styles.disks} style={{ border: 'none', padding: 0, margin: 0 }}>
      <legend className={styles.legend}>{label} data disks</legend>
      {disks.length === 0 && <span className={page.fieldHint}>No data disks. Data disks are optional: none is created unless you add one.</span>}
      {disks.map((disk, index) => {
        const id = `${idPrefix}-disk-${index + 1}`
        const own = problems[disk.key]
        return (
          <div key={disk.key} className={styles.diskRow} role="group" aria-label={`${label} data disk ${index + 1}`}>
            <div className={page.formGroup}>
              <label htmlFor={`${id}-lun`} className={page.label}>LUN</label>
              <input
                id={`${id}-lun`}
                className={page.input}
                type="number"
                min={0}
                max={63}
                value={disk.lun}
                onChange={(event) => patch(disk.key, { lun: Number.parseInt(event.target.value, 10) })}
              />
            </div>
            <div className={page.formGroup}>
              <label htmlFor={`${id}-letter`} className={page.label}>Drive</label>
              <select id={`${id}-letter`} className={page.select} value={disk.driveLetter.toUpperCase()} onChange={(event) => patch(disk.key, { driveLetter: event.target.value })}>
                {DRIVE_LETTERS.map((letter) => (
                  <option key={letter} value={letter}>{letter}:</option>
                ))}
              </select>
            </div>
            <div className={page.formGroup}>
              <label htmlFor={`${id}-size`} className={page.label}>Size (GB)</label>
              <input
                id={`${id}-size`}
                className={page.input}
                type="number"
                min={1}
                max={32767}
                value={Number.isNaN(disk.sizeGb) ? '' : disk.sizeGb}
                onChange={(event) => patch(disk.key, { sizeGb: Number.parseInt(event.target.value, 10) })}
              />
            </div>
            <div className={page.formGroup}>
              <label htmlFor={`${id}-sku`} className={page.label}>Disk type</label>
              <select id={`${id}-sku`} className={page.select} value={disk.sku} onChange={(event) => patch(disk.key, { sku: event.target.value as DataDisk['sku'] })}>
                {SKUS.map((sku) => (
                  <option key={sku.value} value={sku.value}>{sku.label}</option>
                ))}
              </select>
            </div>
            <div className={page.formGroup}>
              <label htmlFor={`${id}-caching`} className={page.label}>Caching</label>
              <select id={`${id}-caching`} className={page.select} value={disk.caching} onChange={(event) => patch(disk.key, { caching: event.target.value as DataDisk['caching'] })}>
                {CACHING.map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              aria-label={`Remove ${label} data disk ${index + 1}`}
              icon={<Trash2 size={14} />}
              onClick={() => onChange(disks.filter((item) => item.key !== disk.key))}
            />
            {own && (
              <ul role="alert" className={styles.diskProblems}>
                {own.map((message) => (
                  <li key={message}>{message}</li>
                ))}
              </ul>
            )}
          </div>
        )
      })}
      <div>
        <Button type="button" variant="secondary" size="sm" icon={<Plus size={14} />} onClick={add}>
          Add data disk
        </Button>
      </div>
    </fieldset>
  )
}
